import React, { useState, useEffect } from 'react';
import { ArrowRight, Shield, Lock, Terminal, MessageSquare, Code, Users, Globe, Award, Zap, Cloud } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import TextCarousel from '@/components/ui/TextCarousel';

const ProfessionalHero = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeService, setActiveService] = useState(0);
  const [stats, setStats] = useState({ assessments: 0, vulnerabilities: 0, satisfaction: 0 });
  const navigate = useNavigate();

  // Services data
  const services = [
    { name: "Application Security", icon: Code, color: "#00a86b", description: "SAST, DAST, SCA & Secure Code Review", link: "/services/application-security" },
    { name: "DevSecOps", icon: Terminal, color: "#06b6d4", description: "CI/CD Pipeline Security Integration", link: "/services/devsecops" },
    { name: "Offensive Security", icon: Shield, color: "#fbbf24", description: "Penetration Testing & Red Teaming", link: "/services/offensive-security" },
    { name: "Cloud Security", icon: Cloud, color: "#3b82f6", description: "Cloud Security Posture Management", link: "/services/cloud-security" },
    { name: "Security Advisory", icon: Users, color: "#a855f7", description: "Cybersecurity Consulting Services", link: "/services/security-advisory" }
  ];

  // Excellence text rotation
  const excellenceTexts = [
    "Operational Excellence",
    "Risk Management", 
    "Subject Matter Experts",
    "Professional Services"
  ];

  // Trigger main animation on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Start counters after main animation completes
  useEffect(() => {
    if (!isLoaded) return;
    
    const startCounters = setTimeout(() => {
      const duration = 2000;
      const fps = 60;
      const increment = duration / fps;
      const steps = Math.floor(duration / increment);
      
      const targets = {
        assessments: 500,
        vulnerabilities: 15000,
        satisfaction: 99.8
      };
      
      let currentStep = 0;
      const timer = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        
        setStats({
          assessments: Math.floor(targets.assessments * easeOutQuart),
          vulnerabilities: Math.floor(targets.vulnerabilities * easeOutQuart),
          satisfaction: Math.floor(targets.satisfaction * easeOutQuart * 10) / 10
        });
        
        if (currentStep >= steps) {
          clearInterval(timer);
          setStats(targets);
        }
      }, increment);
      
      return () => clearInterval(timer);
    }, 1200); // Start after main animation

    return () => clearTimeout(startCounters);
  }, [isLoaded]);

  // Rotate services after animation
  useEffect(() => {
    if (!isLoaded) return;
    
    const startRotation = setTimeout(() => {
      const interval = setInterval(() => {
        setActiveService((prev) => (prev + 1) % services.length);
      }, 3000);
      return () => clearInterval(interval);
    }, 1500);

    return () => clearTimeout(startRotation);
  }, [isLoaded, services.length]);


  return (
    <>
      <style>
        {`
          @keyframes scaleInFromCenter {
            from {
              opacity: 0;
              transform: scale(0.3) translateY(50px);
            }
            to {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }

          @keyframes fadeInBackground {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          @keyframes float {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
            }
            33% {
              transform: translateY(-10px) rotate(-1deg);
            }
            66% {
              transform: translateY(-5px) rotate(1deg);
            }
          }

          @keyframes pulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.8;
              transform: scale(1.05);
            }
          }

          @keyframes gradientShift {
            0%, 100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
          }

          @keyframes borderFlow {
            0%, 100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
          }

          .animate-scaleIn {
            animation: scaleInFromCenter 1.2s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
          }

          .animate-background {
            animation: fadeInBackground 0.8s ease-out forwards;
          }

          .animate-float {
            animation: float 6s ease-in-out infinite;
          }

          .animate-pulse {
            animation: pulse 2s ease-in-out infinite;
          }

          .gradient-text {
            background: linear-gradient(135deg, #00a86b 0%, #06b6d4 50%, #00a86b 100%);
            background-size: 200% 200%;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            color: #00a86b;
            position: relative;
            z-index: 2;
            text-shadow: 0 0 8px rgba(0, 168, 107, 0.3);
            filter: drop-shadow(0 0 4px rgba(0, 168, 107, 0.4));
            animation: gradientShift 8s ease infinite;
          }

          .gradient-border {
            position: relative;
            background: #0f0f17;
            border-radius: 16px;
          }

          .gradient-border::before {
            content: '';
            position: absolute;
            inset: -2px;
            border-radius: 16px;
            padding: 2px;
            background: linear-gradient(135deg, #00a86b, #06b6d4, #fbbf24, #a855f7);
            background-size: 400% 400%;
            -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            -webkit-mask-composite: xor;
            mask-composite: exclude;
            animation: borderFlow 8s linear infinite;
          }

          .service-card {
            transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
            transform-style: preserve-3d;
            position: relative;
          }

          .service-card:hover {
            transform: translateY(-8px);
          }

          .hover-glow {
            position: relative;
            overflow: hidden;
          }

          .hover-glow::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
            transition: left 0.6s;
          }

          .hover-glow:hover::before {
            left: 100%;
          }

          .hover-glow:hover {
            box-shadow: 0 0 40px rgba(0, 168, 107, 0.4);
          }


          .stats-number {
            font-variant-numeric: tabular-nums;
          }

          .content-wrapper {
            transform-origin: center center;
          }

          @media (max-width: 768px) {
            .service-card:hover {
              transform: translateY(-4px);
            }
          }
        `}
      </style>

      <div className="relative min-h-screen bg-[#0f0f17] overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/cyber1.jpg" 
            alt="Cybersecurity Background"
            className="w-full h-full object-cover opacity-80"
          />
          <div className="absolute inset-0 bg-[#0f0f17] bg-opacity-70" />
        </div>
        
        {/* Premium background with smooth gradients */}
        <div className={`absolute inset-0 z-10 ${isLoaded ? 'animate-background' : 'opacity-0'}`}>
          {/* Base gradient overlay - reduced opacity to show background */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f0f17]/80 via-[#0a0a12]/60 to-[#0f0f17]/80" />
          
          
          {/* Subtle center glow */}
          <div 
            className="absolute top-1/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl h-96 opacity-40"
            style={{
              background: 'radial-gradient(ellipse 800px 300px at center, rgba(0, 168, 107, 0.03) 0%, transparent 70%)',
              filter: 'blur(40px)'
            }}
          />
          
          {/* Clean geometric overlay */}
          <div 
            className="absolute inset-0 opacity-[0.015]"
            style={{
              backgroundImage: `
                radial-gradient(circle at 25% 25%, rgba(0, 168, 107, 0.4) 1px, transparent 1px),
                radial-gradient(circle at 75% 75%, rgba(6, 182, 212, 0.3) 1px, transparent 1px)
              `,
              backgroundSize: '120px 120px, 80px 80px'
            }}
          />
        </div>

        {/* Main content - all animates together */}
        <div className={`relative z-10 content-wrapper ${isLoaded ? 'animate-scaleIn' : 'opacity-0'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-12">
            <div className="text-center">
              {/* Logo */}
              <div className="flex items-center justify-center mb-8">
                <div className="relative">
                  <img 
                    src="/images/cipher_character_notext.png" 
                    alt="CIPHER Security"
                    className={`w-28 h-28 md:w-36 md:h-36 ${isLoaded ? 'animate-float' : ''}`}
                    style={{
                      animationDelay: '1.2s',
                      objectFit: 'contain'
                    }}
                  />
                </div>
              </div>

              {/* Main heading */}
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold mb-8 sm:mb-6">
                <span className="text-white inline-block">Cybersecurity</span>
                <br />
                <div className="px-2 sm:px-4 md:px-0 -mt-4 sm:-mt-8 md:-mt-12">
                  <TextCarousel 
                    texts={excellenceTexts}
                    interval={3500}
                    textClassName="gradient-text text-center text-2xl sm:text-4xl md:text-7xl leading-tight"
                    wrapperStyle={{ 
                      minHeight: '2.4em',
                      height: 'auto',
                      lineHeight: '1.1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  />
                </div>
              </h1>

              {/* Subheading */}
              <p className="text-lg sm:text-xl md:text-2xl mb-8 max-w-4xl mx-auto text-gray-300 px-4 sm:px-0">
                Enterprise-grade security solutions delivered by industry experts. We help secure your brand and reputation in the cyberspace.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <a 
                  href="/services"
                  className="group relative px-8 py-4 bg-gradient-to-r from-green-500 to-cyan-500 text-white font-semibold rounded-lg hover-glow transition-all duration-300 hover:scale-105 overflow-hidden"
                >
                  <span className="relative z-10 flex items-center justify-center">
                    Explore Our Services
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform" />
                  </span>
                </a>
                <a 
                  href="/contactus"
                  className="group relative px-8 py-4 text-green-400 font-semibold rounded-lg transition-all duration-300 gradient-border hover:scale-105"
                >
                  <span className="relative z-10 flex items-center justify-center">
                    Schedule Consultation
                    <MessageSquare className="ml-2 w-5 h-5 group-hover:rotate-12 transition-transform" />
                  </span>
                </a>
              </div>

              {/* Service cards */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-16">
                {services.map((service, index) => {
                  const Icon = service.icon;
                  const isActive = index === activeService;
                  
                  return (
                    <div
                      key={index}
                      className={`service-card p-6 rounded-xl border backdrop-blur-sm cursor-pointer transition-all duration-300 hover:scale-110 ${
                        isActive ? 'border-opacity-50 scale-105' : 'border-opacity-20'
                      }`}
                      style={{
                        borderColor: service.color,
                        backgroundColor: isActive ? `${service.color}15` : `${service.color}08`
                      }}
                      onClick={() => navigate(service.link)}
                    >
                      <div className="relative">
                        <Icon 
                          className={`w-10 h-10 mb-4 mx-auto transition-all duration-300 ${
                            isActive && isLoaded ? 'scale-110 animate-pulse' : ''
                          }`}
                          style={{ color: service.color }}
                        />
                        {isActive && isLoaded && (
                          <div 
                            className="absolute inset-0 w-10 h-10 mx-auto blur-xl"
                            style={{ backgroundColor: service.color, opacity: 0.3 }}
                          />
                        )}
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-2">
                        {service.name}
                      </h3>
                      <p className="text-sm text-gray-400 transition-all duration-300">
                        {isActive && isLoaded ? service.description : service.description}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Stats section */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                <div className="text-center group cursor-pointer">
                  <div className="text-4xl font-bold gradient-text mb-2 stats-number group-hover:scale-110 transition-transform">
                    {stats.assessments}+
                  </div>
                  <div className="text-gray-400 group-hover:text-gray-300 transition-colors">Security Assessments</div>
                </div>
                <div className="text-center group cursor-pointer">
                  <div className="text-4xl font-bold gradient-text mb-2 stats-number group-hover:scale-110 transition-transform">
                    {(stats.vulnerabilities / 1000).toFixed(0)}K+
                  </div>
                  <div className="text-gray-400 group-hover:text-gray-300 transition-colors">Vulnerabilities Identified</div>
                </div>
                <div className="text-center group cursor-pointer">
                  <div className="text-4xl font-bold gradient-text mb-2 stats-number group-hover:scale-110 transition-transform">
                    {stats.satisfaction}%
                  </div>
                  <div className="text-gray-400 group-hover:text-gray-300 transition-colors">Client Satisfaction</div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Bottom gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0f0f17] via-[#0f0f17]/80 to-transparent" />
      </div>
    </>
  );
};

export default ProfessionalHero;